import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * Tooltip ligero. Crea un `<span role="tooltip">` en `document.body` al
 * hacer hover/focus sobre el host y lo posiciona con `getBoundingClientRect`.
 *
 * Estilos siguen `docs/visual-rules.md`: fondo navy (`--color-secondary`),
 * texto blanco, sin `border-radius` (regla no negociable de PS).
 */
@Directive({
  selector: '[appTooltip]',
})
export class TooltipDirective {
  readonly appTooltip = input.required<string>();
  readonly appTooltipPlacement = input<'top' | 'bottom'>('top');

  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private tooltipEl: HTMLElement | null = null;
  private readonly tooltipId = `tt-${Math.random().toString(36).slice(2, 9)}`;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    const host = this.hostRef.nativeElement;
    const offMouseEnter = this.renderer.listen(host, 'mouseenter', () => this.show());
    const offMouseLeave = this.renderer.listen(host, 'mouseleave', () => this.hide());
    const offFocus = this.renderer.listen(host, 'focusin', () => this.show());
    const offBlur = this.renderer.listen(host, 'focusout', () => this.hide());
    this.destroyRef.onDestroy(() => {
      offMouseEnter();
      offMouseLeave();
      offFocus();
      offBlur();
      this.hide();
    });
  }

  private show(): void {
    if (this.tooltipEl) return;
    const text = this.appTooltip();
    if (!text) return;

    const el = this.renderer.createElement('span') as HTMLElement;
    this.renderer.setAttribute(el, 'role', 'tooltip');
    this.renderer.setAttribute(el, 'id', this.tooltipId);
    this.renderer.appendChild(el, this.renderer.createText(text));

    Object.assign(el.style, {
      position: 'fixed',
      zIndex: '10000',
      maxWidth: '260px',
      padding: 'var(--space-2) var(--space-3)',
      background: 'var(--color-action-secondary)',
      color: 'var(--color-text-inverse)',
      font: 'var(--font-weight-body-medium) var(--text-size-sm)/var(--leading-snug) var(--font-family-body)',
      pointerEvents: 'none',
      boxShadow: 'var(--shadow-dropdown)',
      borderRadius: 'var(--radius-product-card)',
      opacity: '0',
      transition: 'opacity var(--transition-fast)',
      whiteSpace: 'normal',
      textAlign: 'center',
    } satisfies Partial<CSSStyleDeclaration>);

    this.renderer.appendChild(this.document.body, el);
    this.tooltipEl = el;
    this.position();
    this.renderer.setAttribute(
      this.hostRef.nativeElement,
      'aria-describedby',
      this.tooltipId,
    );
    requestAnimationFrame(() => {
      if (this.tooltipEl) this.tooltipEl.style.opacity = '1';
    });
  }

  private hide(): void {
    if (!this.tooltipEl) return;
    this.renderer.removeChild(this.document.body, this.tooltipEl);
    this.tooltipEl = null;
    this.renderer.removeAttribute(this.hostRef.nativeElement, 'aria-describedby');
  }

  private position(): void {
    if (!this.tooltipEl) return;
    const hostRect = this.hostRef.nativeElement.getBoundingClientRect();
    const ttRect = this.tooltipEl.getBoundingClientRect();
    const placement = this.appTooltipPlacement();
    const margin = 8;

    let top =
      placement === 'top'
        ? hostRect.top - ttRect.height - margin
        : hostRect.bottom + margin;
    let left = hostRect.left + hostRect.width / 2 - ttRect.width / 2;

    const vw = this.document.defaultView?.innerWidth ?? 0;
    if (left < 8) left = 8;
    if (left + ttRect.width > vw - 8) left = vw - ttRect.width - 8;
    // Si el tooltip arriba se sale del viewport, voltear a abajo.
    if (top < 8 && placement === 'top') {
      top = hostRect.bottom + margin;
    }

    this.tooltipEl.style.top = `${top}px`;
    this.tooltipEl.style.left = `${left}px`;
  }
}
